import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-named-as-default
import List from 'react-virtualized/dist/commonjs/List';
// Import UI Components
import { Empty, Option } from '@geneui/components';
import PartnerRow from './PartnerRow';
// Import Constants
import { l } from 'constants/common';
//Import SCSS
import 'assets/scss/partners.scss';

const PartnersListWithPartnerGroup = ({
    data,
    groupIndex,
    listWidth,
    listHeight,
    listHeightWithGroupLabel,
    listRowHeight,
    selectHandler,
    withGroupLabel,
    selectedPropertyKey,
    groupHeightsRef,
    hasSearchValue,
    selectedPartner,
}) => {
    const { t } = useTranslation();

    const getGroupRow = useMemo(() => {
        return <span>{`${data.groupName} (${data.items.length})`}</span>;
    }, [data]);

    const calculatedListHeight = useMemo(() => {
        if (data.items.length >= 5) {
            if (withGroupLabel) {
                return listHeightWithGroupLabel;
            } else {
                return listHeight;
            }
        } else {
            return listRowHeight * data.items.length;
        }
    }, [data.items.length, listHeight, listHeightWithGroupLabel, listRowHeight, withGroupLabel]);

    useEffect(() => {
        groupHeightsRef.current[groupIndex] = calculatedListHeight;
    }, [calculatedListHeight, groupIndex, groupHeightsRef]);

    useEffect(() => {
        const itemIndex = data.items.findIndex((item) => item[selectedPropertyKey] === selectedPartner);
        const scrollableContainerList = document.querySelectorAll('.virtualized-partner-list')[groupIndex];
        let scrollableGroupContainer;
        if (withGroupLabel) {
            scrollableGroupContainer = document.querySelector('.partners-group-wrapper');
        }
        if (hasSearchValue) {
            if (withGroupLabel) {
                scrollableGroupContainer.scrollTo(0, 0);
            }
            setTimeout(() => scrollableContainerList.scrollTo(0, 0), 0);
        } else {
            if (itemIndex !== -1 && scrollableContainerList) {
                if (withGroupLabel) {
                    const previousGroupsHeight = groupHeightsRef.current.reduce((acc, val, index) => {
                        if (index < groupIndex) {
                            acc += val + 30;
                        }
                        return acc;
                    }, 0);
                    scrollableGroupContainer.scrollTo(0, previousGroupsHeight);
                }
                scrollableContainerList.scrollTo(0, itemIndex * listRowHeight);
            }
        }
    }, [
        selectedPartner,
        data.items,
        groupIndex,
        listRowHeight,
        selectedPropertyKey,
        withGroupLabel,
        groupHeightsRef,
        hasSearchValue,
    ]);

    return (
        <div className="partner-group">
            {withGroupLabel && !isEmpty(data.items) && (
                <Option leftCustomElement={getGroupRow} className="partner-group-label" sticky="top" />
            )}
            {isEmpty(data.items) ? (
                <Empty title={t(l.NoDataToDisplay)} size="small" />
            ) : (
                <List
                    width={listWidth}
                    height={calculatedListHeight}
                    rowCount={data.items.length}
                    rowHeight={listRowHeight}
                    className="virtualized-partner-list"
                    rowRenderer={({ index, style }) => {
                        const currentItem = data.items[index];
                        return (
                            <Option
                                style={style}
                                key={`${currentItem[selectedPropertyKey]}_${currentItem.label}`}
                                leftCustomElement={<PartnerRow data={currentItem} />}
                                onClick={() => selectHandler(currentItem)}
                                active={currentItem[selectedPropertyKey] === selectedPartner}
                            />
                        );
                    }}
                />
            )}
        </div>
    );
};

PartnersListWithPartnerGroup.propTypes = {
    data: PropTypes.object,
    groupIndex: PropTypes.number,
    selectedPropertyKey: PropTypes.string,
    listWidth: PropTypes.number,
    listHeight: PropTypes.number,
    listHeightWithGroupLabel: PropTypes.number,
    listRowHeight: PropTypes.number,
    selectHandler: PropTypes.func.isRequired,
    withGroupLabel: PropTypes.bool,
    groupHeightsRef: PropTypes.object.isRequired,
    hasSearchValue: PropTypes.bool,
    selectedPartner: PropTypes.number,
};

PartnersListWithPartnerGroup.defaultProps = {
    groupIndex: -1,
    listWidth: 258,
    listHeightWithGroupLabel: 165,
    listHeight: 250,
    listRowHeight: 40,
    selectedPropertyKey: 'value',
    withGroupLabel: true,
};

export default PartnersListWithPartnerGroup;
